import { Location } from "@angular/common";
import _ from "lodash";
import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";

import { CellClickedEvent, GridReadyEvent, RowSelectedEvent, RowSelectionOptions } from "@ag-grid-community/core";
import { ActivatedRoute } from "@angular/router";
import {
  ActiveStatusEnum,
  Feature,
  FeatureChoice,
  Product,
  ProductFeatureChoice,
  ProductType,
  ProductTypeFeatureMap,
} from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox, RowSelectionSingle } from "app/shared/ag-fns";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { takeUntil } from "rxjs/operators";

import { AgStyleFns } from "app/shared/ag-style-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";

import { UtilFns } from "app/shared/util-fns";
import { EditFeatureChoiceDialogComponent } from "./edit-feature-choice-dialog.component";
import { EditFeatureDialogComponent } from "./edit-feature-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";
import { ApplyCoreChoicesPreviewDialogComponent } from "./apply-core-choices-preview-dialog.component";

@Component({
  selector: "feature",
  templateUrl: "./feature.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FeatureComponent extends DomainBaseComponent implements OnInit {
  @ViewChild("templateButtonsCell") templateButtonsCell!: TemplateRef<any>;
  @ViewChild("templateMappingButtonsCell")
  templateMappingButtonsCell!: TemplateRef<any>;
  @ViewChild("productTypeButtonsCell") productTypeButtonsCell!: TemplateRef<any>;

  featureGridOptions!: GridOptionsExt;
  allFeatures: Feature[] = [];
  features!: Feature[];

  selectedFeature!: Feature;

  featureChoiceGridOptions!: GridOptionsExt;
  allFeatureChoices: FeatureChoice[] = [];
  featureChoices: FeatureChoice[] = [];
  selectedFeatureChoice?: FeatureChoice;
  allowCombination!: boolean;
  featureChoiceRowSelection: RowSelectionOptions = RowSelectionSingle;

  shouldShowInactive: boolean = false;
  populateCoreChoices: boolean = false;

  /**
   * A choice id from the `fcid` query param, consumed once.
   *
   * Callers that deep link here want a named choice selected rather than the first row -
   * the Manufacturer Feed page sends operators here so they can see which products use a
   * color before they rename it.
   */
  private pendingFeatureChoiceId: number | null = null;

  productGridOptions!: GridOptionsExt;
  // migrationProductTypeIdSet: Set<number> = new Set();

  productTypeGridOptions!: GridOptionsExt;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    protected location: Location
  ) {
    super(domainService);
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    const fcid = this.route.snapshot.queryParamMap.get("fcid");
    this.pendingFeatureChoiceId = fcid ? +fcid : null;

    this.featureGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onFeatureGridReady,
      onRowSelected: this.onFeatureRowSelected,
    });
    // AgFns.captureGridRouteParams(this.featureGridOptions, this.route, "id");
    this.captureLocation(this.featureGridOptions, "id");

    this.featureChoiceGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onFeatureChoiceGridReady,
      onRowSelected: this.onFeatureChoiceRowSelected,
    });

    this.productGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProductGridReady,
      rowModelType: "serverSide",
    });

    this.productTypeGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProductTypeGridReady,
      rowModelType: "serverSide",
    });

    this.allFeatures = await this.dbQueryService.getFeatures();

    this.isPageReady = true;
  }

  async canDeactivate() {
    if (!this.featureChoiceGridOptions || !this.featureChoiceGridOptions.apiX) return;
    this.featureChoiceGridOptions.apiX.stopEditing();
    const ok = await super.canDeactivate();
    if (!ok) return false;
    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog("Changes have not been saved", "Do you want to save before exiting?");
      if (ok) {
        await this.dbSaveService.saveChanges();
      } else {
        this.dbSaveService.rejectChanges();
      }
    }

    await UtilFns.wait(1);
    this.uow.clearEntities(Feature);
    this.uow.clearEntities(FeatureChoice);
    this.uow.clearEntities(ProductType);
    this.uow.clearEntities(Product); // lots of these could have been queried.
    this.uow.clearEntities(ProductFeatureChoice); // lots of these could have been queried.
    this.uow.clearEntities(ProductTypeFeatureMap);
    return true;
  }

  refreshFeatures() {
    this.features = this.shouldShowInactive
      ? this.allFeatures
      : this.allFeatures.filter((x) => x.activeStatusId != ActiveStatusEnum.Inactive);
    AgFns.refreshGrid(this.featureGridOptions, this.features);

    this.featureChoices = this.shouldShowInactive
      ? this.allFeatureChoices
      : this.allFeatureChoices.filter((x) => x.activeStatusId != ActiveStatusEnum.Inactive);

    // Don't use selectDefaultRow here because the 'selected' FeatureChoice' may no longer be visible.
    // AgFns.selectFirstRow(this.featureChoiceGridOptions);
  }

  @wrapBusy
  async refreshFeatureChoices(featureId: number) {
    this.allFeatureChoices = await this.dbQueryService.getFeatureChoices(featureId);
    this.featureChoices = this.shouldShowInactive
      ? this.allFeatureChoices
      : this.allFeatureChoices.filter((x) => x.activeStatusId != ActiveStatusEnum.Inactive);
    await AgFns.refreshGrid(this.featureChoiceGridOptions, this.featureChoices);

    await this.selectPendingOrDefaultChoice();
  }

  /**
   * Select the choice a deep link asked for, otherwise fall back to the first row.
   *
   * Two callers reach this, and onFeatureRowSelected does not await refreshFeatureChoices -
   * so the first pass can run before the grid has its rows. The id is therefore cleared only
   * once a row actually matched, which lets whichever pass sees the rows win. After that a
   * later refresh - an edit, a combine, a cancel - no longer drags the selection back.
   */
  private async selectPendingOrDefaultChoice() {
    const id = this.pendingFeatureChoiceId;
    if (id != null) {
      const node = await AgFns.selectGridRowByKey(
        this.featureChoiceGridOptions,
        (fc: FeatureChoice) => String(fc.id),
        String(id)
      );
      if (node != null) {
        this.pendingFeatureChoiceId = null;
        return;
      }
    }
    await AgFns.selectDefaultRow(this.featureChoiceGridOptions);
  }

  async onFeatureGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.editFeature.bind(this), {
          label: "Edit",
        }),
        ...AgFns.fixWidth(60),
      },
      {
        headerName: "Feature Name",
        field: "name",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(150),
      },
      {
        headerName: "Document Name",
        valueGetter: (params) => (params.data.docName ? params.data.docName : params.data.name),
        ...AgFns.flexWidth(75),
      },
      {
        headerName: "Feature Type",
        field: "featureKind.name",
        filter: "agSetColumnFilter",
        ...AgFns.flexWidth(100),
      },
      {
        ...UniAgFns.getActiveStatusColDef("activeStatus.name"),
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(120),
      },
      // { headerName: "Feature Id", field: "id", filter: "agNumberColumnFilter" },
    ];
    // show buttons iff user has Inventory role

    if (this.user.isProductAdmin) {
      const invColDefs = [
        {
          ...AgFns.createButtonProps("", this.addFeatureChoice.bind(this), {
            label: "Add Feature Choice",
          }),
          ...AgFns.fixWidth(150),
        },

        // { ...AgFns.createButtonProps('', this.updateFeatureChangeMapping.bind(this), { label: 'Update Feature Conversion Table'} ) },
        // No longer supported
        // { ...AgFns.createButtonProps('', this.deleteFeature.bind(this), { label: 'Delete Template'} ) },
      ];
      colDefs.splice(2, 0, ...(invColDefs as any[]));
    }

    const sortModel = [{ colId: "name", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);
    this.refreshFeatures();

    await this.applyLocation();
  }

  async onFeatureChoiceGridReady(event: GridReadyEvent) {
    const cellStyle = this.user.isProductAdmin ? AgStyleFns.setEditableStyle : (p) => null;
    const colDefs = [
      this.user.isProductAdmin && {
        ...AgFns.createButtonProps("", this.editFeatureChoice.bind(this), {
          label: "Edit",
        }),
        colId: "edit",
        ...AgFns.fixWidth(60),
      },
      {
        headerName: "Value",
        field: "choiceValue",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(150),
      },
      {
        headerName: "Reorder Value",
        colId: "reorderValue",
        filter: "agTextColumnFilter",
        editable: this.user.isProductAdmin,
        cellStyle: cellStyle,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: (params: any) => {
          const current = params.data as FeatureChoice;
          const options = this.featureChoices
            .filter((fc) => fc.id !== current?.id && fc.activeStatusId === ActiveStatusEnum.Active)
            .map((fc) => fc.choiceValue);
          return { values: ["(none)", ..._.sortBy(options)] };
        },
        valueGetter: (params: any) => (params.data as FeatureChoice)?.reorderFeatureChoice?.choiceValue ?? "",
        valueSetter: (params: any) => {
          const row = params.data as FeatureChoice;
          if (!row) return false;
          const newVal = params.newValue;
          if (newVal == null || newVal === "" || newVal === "(none)") {
            row.reorderFeatureChoiceId = null;
            return true;
          }
          const target = this.featureChoices.find((fc) => fc.choiceValue === newVal && fc.id !== row.id);
          row.reorderFeatureChoiceId = target?.id ?? null;
          return true;
        },
        ...AgFns.flexWidth(100),
      },
      {
        headerName: "Order",
        field: "displayOrder",
        filter: "agTextColumnFilter",
        editable: this.user.isProductAdmin,
        cellStyle: cellStyle,
        type: "numericColumn",
        ...AgFns.fixWidth(90),
      },

      // { ...AgFns.createButtonProps('', this.migrateProductTypes.bind(this), { label: 'Migrate To..' }) ,  minWidth: 120 },
      {
        ...UniAgFns.getActiveStatusColDef("activeStatus.name"),
        ...AgFns.fixWidth(100),
      },
    ];
    // show buttons iff user has Inventory role
    if (this.user.isProductAdmin) {
      const invColDefs = [
        {
          ...AgFns.createButtonProps("", this.onCombine.bind(this), {
            label: "Combine",
            canDisplay: this.canCombine.bind(this),
          }),
          colId: "combine",
          ...AgFns.fixWidth(100),
        },

        {
          ...AgFns.createButtonProps("", this.toggleActive.bind(this), {
            label: "Toggle Status",
            canDisplay: (p) => this.allowCombination,
          }),
          colId: "toggle",
          ...AgFns.fixWidth(120),
        },
      ];
      colDefs.splice(3, 0, ...(invColDefs as any[]));
    }
    const sortModel = [{ colId: "displayOrder", sort: "asc" as const }];
    AgFns.initGrid(event, colDefs, sortModel);
    this.onAllowCombinationChange();
  }

  toggleActive(fc: FeatureChoice) {
    fc.activeStatusId = UniAgFns.nextToggleStatus(fc.activeStatusId);
    this.featureChoiceGridOptions.apiX!.refreshCells();
  }

  async onProductGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.editProductType.bind(this), {
          label: "View",
        }),
        ...AgFns.fixWidth(60),
      },
      { ...UniAgFns.getManufacturerColDef("productType.manufacturer.name"), ...AgFns.flexWidth(120) },
      { ...UniAgFns.getProductTypeSKUColDef("productType.style"), ...AgFns.flexWidth(80) },
      { ...UniAgFns.getProductTypeDescriptionColDef("productType.description"), ...AgFns.flexWidth(120) },
      { ...UniAgFns.getFeatureChoicesColDef("featureChoicesExtract"), ...AgFns.flexWidth(120) },

      { ...UniAgFns.getActiveStatusColDef("activeStatus.name"), ...AgFns.fixWidth(120) },
      // { headerName: 'Product Type Id', field: 'productTypeId', filter: 'agNumberColumnFilter' },
    ];

    const sortModel = [
      { colId: "productType.manufacturer.name", sort: "asc" },
      { colId: "productType.description", sort: "asc" },
      { colId: "productType.style", sort: "asc" },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onProductTypeGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.editProductType.bind(this), {
          label: "View",
        }),
        ...AgFns.fixWidth(60),
      },
      { ...UniAgFns.getManufacturerColDef("manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("description") },

      { headerName: "Feature Names", field: "featureNamesExtract", ...AgFns.flexWidth(150) },
      { ...UniAgFns.getActiveStatusColDef("activeStatus.name"), ...AgFns.fixWidth(120) },

      // { headerName: 'Product Type Id', field: 'id', filter: 'agNumberColumnFilter' },
    ];
    const sortModel = [
      { colId: "manufacturer.name", sort: "asc" },
      { colId: "style", sort: "asc" },
      { colId: "description", sort: "asc" },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onFeatureRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) return;

    const feature = e.data as Feature;
    if (!feature) return;

    this.selectedFeature = feature;
    this.replaceLocation(feature.id);
    this.populateCoreChoices = false;
    this.updateFeatureChoiceGridMode();

    await AgFns.busyGrid([this.featureChoiceGridOptions, this.productTypeGridOptions], this, async () => {
      this.refreshFeatureChoices(feature.id);
    });
    this.setProductTypesDatasource();

    await UtilFns.wait(100);
    if (this.featureChoices.length > 0) {
      await this.selectPendingOrDefaultChoice();
    } else {
      this.selectedFeatureChoice = undefined;
      this.setFeatureChoiceQueryDatasource();
    }
  }

  private setProductTypesDatasource() {
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createProductTypesForFeatureQuery(this.selectedFeature.id));
    this.productTypeGridOptions.apiX?.setGridOption("serverSideDatasource", ds);
  }

  async onFeatureChoiceRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) return;

    const fc = e.data as FeatureChoice;
    if (!fc) return;

    // Block selecting inactive choices in core mode
    if (this.populateCoreChoices && fc.activeStatusId !== ActiveStatusEnum.Active) {
      e.node.setSelected(false);
      return;
    }

    await this.refreshFeatureChoice(fc);
  }

  private async refreshFeatureChoice(fc: FeatureChoice) {
    this.selectedFeatureChoice = fc;
    this.setFeatureChoiceQueryDatasource();
  }

  private setFeatureChoiceQueryDatasource() {
    // -1 is to populate an 'empty' datasource.
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createProductsForFeatureChoiceQuery(this.selectedFeatureChoice?.id ?? -1));

    this.productGridOptions.apiX.setGridOption("serverSideDatasource", ds);
  }

  async onTabChanged(evt: any) {
    await UtilFns.wait(100);
    // hack: because of ag-grid issues with button redraw on ngIf'd tab.
    if (evt.index === 0) {
      // Feature Choices tab
      AgFns.sizeColumnsToFit(this.featureChoiceGridOptions);
      AgFns.sizeColumnsToFit(this.productGridOptions);
    } else if (evt.index === 1) {
      // Product Types tab
      AgFns.sizeColumnsToFit(this.productTypeGridOptions);
    }
  }

  // also handles adds
  async editFeature(feature?: Feature) {
    if (!this.okToPerform()) return;
    const r = await DialogBaseComponent.open(EditFeatureDialogComponent, this, {
      feature: feature,
      dialogWidth: "30vw",
      dialogHeight: "40vh",
    });
    if (r) {
      if (feature == null) {
        this.features = await this.dbQueryService.getFeatures();
      }
      AgFns.refreshGrid(this.featureGridOptions, this.features);
    }
  }

  async addFeatureChoice(feature: Feature) {
    if (!this.okToPerform()) return;
    // insure that featureChoices are populated - because this event can occur before the row select event.
    if (feature.featureChoices.length == 0) {
      await this.refreshFeatureChoices(feature.id);
    }
    const r = await DialogBaseComponent.open(EditFeatureChoiceDialogComponent, this, {
      feature: feature,
      dialogWidth: "30vw",
      dialogHeight: "40vh",
    });
    if (r) {
      await this.refreshFeatureChoices(feature.id);
    }
  }

  async editFeatureChoice(featureChoice: FeatureChoice) {
    if (!this.okToPerform()) return;
    if (featureChoice.feature == null) return;
    const r = await DialogBaseComponent.open(EditFeatureChoiceDialogComponent, this, {
      featureChoice: featureChoice,
      feature: featureChoice.feature,
      // Wide enough for the Product Use panel's two columns.
      dialogWidth: "45vw",
      dialogHeight: "auto",
    });

    if (r) {
      await this.refreshFeatureChoices(featureChoice.feature.id);
    }
  }

  async editProductType(x: ProductType | Product) {
    if (!this.okToPerform()) return;
    const productType = x instanceof ProductType ? x : x.productType;
    if (productType?.id) {
      this.router.navigate(["/references/manage/product-type", productType.id]);
    }
  }

  okToPerform(): boolean {
    if (this.uow.hasChanges()) {
      //this.toastr.warning("You must first save or cancel current changes.", "Cannot Perform Action");
      this.dialogService.showOkMessage("Cannot Continue", `You cannot perform this operation until other changes are first saved.`);
      return false;
    }
    return true;
  }

  onAllowCombinationChange() {
    if (this.allowCombination) this.populateCoreChoices = false;
    this.updateFeatureChoiceGridMode();
  }

  onPopulateCoreChoicesChange() {
    if (this.populateCoreChoices) {
      this.allowCombination = false;
      this.shouldShowInactive = false;
      this.refreshFeatures();
    }
    this.updateFeatureChoiceGridMode();
  }

  private updateFeatureChoiceGridMode() {
    const gridApi = this.featureChoiceGridOptions.apiX;
    const isSpecialMode = this.allowCombination || this.populateCoreChoices;
    gridApi!.setColumnsVisible(["edit"], !isSpecialMode);
    //gridApi!.setColumnsVisible(["combine", "toggle"], this.allowCombination);
    gridApi!.setColumnWidths([{ key: "combine", newWidth: 115 }]);
    gridApi!.setColumnWidths([{ key: "toggle", newWidth: 140 }]);
    this.featureChoiceRowSelection = (this.allowCombination || this.populateCoreChoices)
      ? RowSelectionMultipleCheckbox : RowSelectionSingle;
    gridApi!.setGridOption("isRowSelectable", this.populateCoreChoices
      ? (node) => node.data?.activeStatusId === ActiveStatusEnum.Active
      : () => true);
    gridApi!.deselectAll();
    AgFns.sizeColumnsToFit(this.featureChoiceGridOptions);
  }

  canCombine(combineFc: FeatureChoice) {
    if (this.featureChoiceGridOptions.apiX == null) {
      return false;
    }
    if (this.allowCombination && this.featureChoiceGridOptions.apiX.getSelectedRows().length > 2) {
      this.toastr.success("Only two features may be combined at one time.");
      this.featureChoiceGridOptions.apiX.deselectAll();
      return false;
    }

    return combineFc == this.selectedFeatureChoice && this.featureChoiceGridOptions.apiX.getSelectedRows().length == 2;
  }

  @wrapBusy
  async onCombine(combineFc: FeatureChoice, event?: CellClickedEvent, origEvent?: UIEvent) {
    // Needed to stop onRowSelected call from being made right after this call OR during any await call within this method.
    // The askIfOk call below is async and the OnRowSelected will get called during its execution which will in turn
    // deselect all of the rows except the current one.
    origEvent?.stopPropagation();

    if (!this.okToPerform()) return;

    // if (this.uow.hasChanges()) {
    //   this.dialogService.showOkMessage("Cannot save first", `You cannot perfom this operation until other changes are first saved.. `);
    //   return;
    // }

    if (combineFc == null) {
      return;
    }
    const fromFcs = (this.featureChoiceGridOptions.apiX?.getSelectedRows() as FeatureChoice[]).filter((x) => x != combineFc);
    if (fromFcs.length != 1) {
      return;
    }

    const numProductsAffected = await this.dbQueryService.getProductsCountWithFeatureChoice(fromFcs[0].id);
    if (numProductsAffected > 100) {
      await this.dialogService.showOkMessage(
        "Too many products affected",
        `This operation will change ${numProductsAffected} products.
        This will take too long to process interactively here - please contact your admin`
      );
      return;
    }

    const ok = await this.dialogService.yesNoDialog("Combine Feature Choices", "Are you sure that you want to combine these Feature Choices?");
    if (!ok) return;

    var count;
    await AgFns.busyGrid([this.featureChoiceGridOptions, this.productTypeGridOptions, this.productGridOptions], this, async () => {
      const fromFcsIds = fromFcs.map((x) => x.id);

      count = await this.dbSaveService.combineFeatureChoices(fromFcsIds[0], combineFc.id);
    });

    await this.refreshFeatureChoices(this.selectedFeature.id);

    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductFeatureChoice);
    this.uow.clearEntities(ProductTypeFeatureMap);

    this.productGridOptions.apiX!.refreshServerSide();
    this.productGridOptions.apiX!.refreshCells();

    this.toastr.success(`${count} records updated as a result of combining these feature choices`);
  }

  getSelectedCoreChoiceIds(): number[] {
    const selectedRows = this.featureChoiceGridOptions.apiX?.getSelectedRows() as FeatureChoice[] ?? [];
    return selectedRows.filter(fc => fc.activeStatusId === ActiveStatusEnum.Active).map(fc => fc.id);
  }

  @wrapBusy
  async applyCoreChoices() {
    if (!this.okToPerform()) return;
    if (!this.selectedFeature) return;

    const featureChoiceIds = this.getSelectedCoreChoiceIds();
    if (featureChoiceIds.length === 0) return;

    // 1. Get preview data from server
    const previewItems = await this.dbSaveService.previewApplyCoreFeatureChoices(this.selectedFeature.id, featureChoiceIds);
    if (previewItems.length === 0) {
      await this.dialogService.showOkMessage("No Products to Create",
        "All product types already have products for all selected feature choices.");
      return;
    }

    // 2. Open preview dialog — user can uncheck ProductTypes to skip
    const selectedPtIds = await DialogBaseComponent.open(
      ApplyCoreChoicesPreviewDialogComponent, this, {
        previewItems,
        dialogWidth: "70vw",
        dialogHeight: "70vh",
      }) as number[] | null;
    if (!selectedPtIds || selectedPtIds.length === 0) return;

    // 3. Guardrail — warn on large operations
    const selectedItems = previewItems.filter(p => selectedPtIds.includes(p.productTypeId));
    const totalProducts = _.sumBy(selectedItems, x => x.productCount);
    if (totalProducts > 1000) {
      const confirmed = await this.dialogService.yesNoDialog(
        "Large Operation",
        `This will create ${totalProducts.toLocaleString()} products across ${selectedPtIds.length} product types. ` +
        `This may take a moment. Do you want to proceed?`,
        'Ok',
        'Cancel',
      );
      if (!confirmed) return;
    }

    // 4. Execute for selected ProductTypes only
    const result = await this.dbSaveService.applyCoreFeatureChoices(
      this.selectedFeature.id, featureChoiceIds, selectedPtIds);

    // 5. Clean up caches (server created entities outside Breeze)
    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductFeatureChoice);

    // 6. Refresh grids
    this.productGridOptions.apiX?.refreshServerSide();
    this.toastr.success(
      `${result.ProductsCreated} product(s) created across ${result.ProductTypesAffected} product type(s).`);
  }

  async cancelChanges() {
    if (!(await this.dialogService.yesNoDialog('Cancel Confirmation', 'You have unsaved changes.  Do you still want to cancel?'))) {
      return;
    }
    this.featureChoiceGridOptions.apiX.stopEditing();
    this.uow.rollback();
    await this.refreshFeatureChoices(this.selectedFeature.id);
    this.toastr.success("Changes were cancelled");
  }

  async saveChanges() {
    this.featureChoiceGridOptions.apiX.stopEditing();
    const sr = await this.dbSaveService.saveChanges();
    AgFns.refreshGrid(this.featureChoiceGridOptions, this.featureChoices);
    this.toastr.success("Feature saved");
  }

  // No longer supported - problems with cleaning up history on Proximity if we allow deletion of features or feature choices
  // Use Status change instead.

  // async deleteFeature(template: Feature) {
  //   if (template.activeStatusId >= 1) {
  //     this.dialogWrapperService.showOkMessage('Cannot delete',
  //       `This Item Size Template cannot be deleted once it has become active. `
  //     );
  //     return;
  //   }
  //   const isEmpty = await this.dbQueryService.isEmptyFeature(template.id);
  //   if (!isEmpty) {
  //     this.dialogWrapperService.showOkMessage(
  //       'Cannot delete',
  //       `This Item Size Template cannot be deleted because it is still referenced by at least one ProductType. `
  //     );
  //     return;
  //   }
  //   const yn = await this.dialogWrapperService.askYesNo('Delete?', 'Are you sure?');
  //   if (yn.index === 1) {
  //     return;
  //   }
  //   template.featureChoices.slice().forEach(tm => tm.entityAspect.setDeleted());
  //   template.entityAspect.setDeleted();
  //   await this.saveSingleChange(template);
  // }

  // async deleteFeatureChoice(featureChoice: FeatureChoice) {
  //   if (this.uow.hasChanges()) {
  //     this.dialogWrapperService.showOkMessage('Cannot delete',
  //       `You cannot delete this item until other changes are first saved.. `
  //     );
  //     return;
  //   }
  //   const isInUse = await this.dbQueryService.checkIfInUse(featureChoice, ProductFeatureChoice, 'featureChoiceId');
  //   if (isInUse) {
  //     this.dialogWrapperService.showOkMessage(
  //       'Cannot delete',
  //       `This Feature Choice cannot be deleted from this Feature because it is still referenced by at least one Product. `
  //     );
  //     return;
  //   }
  //   const feature = featureChoice.feature;
  //   featureChoice.entityAspect.setDeleted();
  //   await this.dbSaveService.saveChanges();
  //   this.toastr.success('Feature Choice deleted.');
  //   this.featureChoices = await this.dbQueryService.getFeatureChoices(feature.id);
  //   this.featureChoiceGridOptions.apiX.redrawRows();
  // }

  // private async saveSingleChange(entity: Entity, clearMappings = true) {
  //   const sr = await this.dbSaveService.saveSelectedChanges([entity]);
  //   this.features = await this.dbQueryService.getFeatures();
  //   this.featureGridOptions.apiX.redrawRows();
  //   if (clearMappings) {
  //     this.featureChoices = [];
  //     this.featureChoiceGridOptions.apiX.redrawRows();
  //   }
  // }

  // private async saveMappingChanges(template: Feature) {
  //   await this.dbSaveService.saveChanges();
  //   this.featureChoices = await this.dbQueryService.getFeatureChoices(template.id);
  //   this.featureChoiceGridOptions.apiX.redrawRows();
  // }

  // private async checkIfTemplateExists(templateName: string) {
  //   const foundItem = this.features.find(x => x.name === templateName );
  //   if (foundItem != null) {
  //     await this.dialogWrapperService.showOkMessage('This Template name already exists', 'Templates cannot share the same name');
  //     return true;
  //   } else {
  //     return false;
  //   }
  // }

  // private async checkIfFeatureChoiceExists(choiceValue: string) {
  //   const foundItem = this.featureChoices.find(x => x.choiceValue === choiceValue );
  //   if (foundItem != null) {
  //     await this.dialogWrapperService.showOkMessage(`This 'Use Size' already exists in this template`, 'Duplicate Use Sizes are not allowed');
  //     return true;
  //   } else {
  //     return false;
  //   }
  // }

  // Migration code removed.
  // hasMigrationData(product: Product) {
  //   return this.migrationProductTypeIdSet.has(product.productTypeId);
  // }

  // async migrateProductTypes(featureChoice: FeatureChoice) {

  //   const mfccs = await this.dbQueryService.getMigrationFeatureChoiceChanges(featureChoice.feature._origName);
  //   var origToFeatureNames = _.uniq(mfccs.map(x => x.toOrigFeatureName));
  //   var includedFeatures =  origToFeatureNames.map(n => this.features.find(f => n == f._origName)).filter( x => x != null);
  //   if (includedFeatures.length == 0) {
  //     await this.dialogWrapperService.showOkMessage("Feature has no registered feature conversions", "There are no registered feature conversions for this feature. Please add these conversions first.");
  //     return;
  //   }
  //   const features = await EntityFinderDialog.open(FeatureFinderDialogComponent, this, {
  //     includedFeatureIds: includedFeatures.map(x => x.id),
  //   });
  //   if (features.length != 1) return;
  //   const count = await this.dbSaveService.migrateFeatureChoice(featureChoice.id, features[0].id);
  //   this.toastr.success(`${count} Product Types Migrated`);
  //   await this.refreshFeatureChoice(featureChoice);
  // }
}
